import { useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import {
  Joyride,
  ACTIONS,
  EVENTS,
  STATUS,
  type Step,
  type TooltipRenderProps,
  EventData,
  Controls,
} from "react-joyride";
import { connect } from "react-redux";

import type { OpenAPI, UserMe } from "simplydo/interfaces";
import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";
import { Card, CardContent, CardHeader } from "simplydo/react/ui/card";
import { Divider } from "simplydo/react/ui/divider";
import { H4, Subheader } from "simplydo/react/ui/header";

import actions from "actions";
import api from "api";
import type { AppDispatch, RootState } from "store";
import constants from "utils/constants";

const TooltipButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const TooltipContainer = styled.div``;

type IStepCustomFunction = (stepData: ICustomStep) => void;

export type OrganisationTour = { id: string; isEnabled?: boolean };

export interface ITourStep {
  target: string;
  content: string;
  customTourFunctionId: string;
  placement: Step["placement"];
}

interface ITour {
  key: string;
  value: string;
  title: string;
  description: string;
  steps: ITourStep[];
}

export interface IProductTour {
  user: UserMe | null;
  urlOrganisation: OpenAPI.Schemas["Organisation"] | null;
  tour:
    | "challenge"
    | "assessment"
    | "assessmentHomepage"
    | "innovationIntelligenceList"
    | "orgSwitcher";
  demoingTour: string;
  setDemoingTour: (tourId: string) => void;
  isReady: boolean;
  customTourFunctions?: { [customTourFunctionId: string]: IStepCustomFunction };
  onComplete?: () => void;
}

interface ICustomStep extends Step {
  customTourFunctionId?: string;
  showProgress?: boolean;
  title: string;
  content: string;
}

type ProductTourTooltipProps = TooltipRenderProps & { stepCount: number };

const ProductTourTooltip = ({
  continuous,
  step,
  index,
  primaryProps,
  closeProps,
  backProps,
  tooltipProps,
  stepCount,
}: ProductTourTooltipProps) => (
  <TooltipContainer {...tooltipProps}>
    <Card>
      <CardHeader>
        <H4>{step.title || "Platform guidance"}</H4>
      </CardHeader>
      <CardContent>
        <Subheader className="description">{step.content}</Subheader>
        <Divider hidden />
        <TooltipButtonContainer>
          {index > 0 ? (
            <Button {...backProps} id="back" size="small">
              Back
            </Button>
          ) : (
            <div />
          )}
          <div />
          {continuous ? (
            <Button
              {...primaryProps}
              id="next"
              size="small"
            >{`${index === stepCount - 1 ? "Finish" : "Next"}${step.showProgress ? ` ${index + 1} / ${stepCount}` : ""}`}</Button>
          ) : (
            <Button
              {...closeProps}
              id="close"
              size="small"
            >{`Finish${step.showProgress ? ` ${index + 1} / ${stepCount}` : ""}`}</Button>
          )}
        </TooltipButtonContainer>
      </CardContent>
    </Card>
  </TooltipContainer>
);

/*

~ Creating a Tour ~

- When creating a new tour, the tour must be added to the productTours array in util/constants
- The tour value/id must be added to the interface IProductTour, and to the API under organisations/product_tours into the valid_tours array
- Each tour needs steps with valid classnames that correspond to elements. This is how the tour system knows where to display the tooltip

*/

const ProductTour = ({
  user,
  tour: tourId,
  isReady,
  urlOrganisation,
  customTourFunctions,
  onComplete,
  demoingTour,
  setDemoingTour,
}: IProductTour) => {
  const [organisationTourData, setOrganisationTourData] =
    useState<OrganisationTour>({ id: "", isEnabled: false });
  const { t } = useTranslation();
  const theme = useTheme();
  const [runTour, setRunTour] = useState<boolean>(false);
  const [tourStepIndex, setTourStepIndex] = useState<number>(0);
  const [completedTour, setCompletedTour] = useState<boolean>(false);
  const orgId: string =
    user?.ownerOrganisation?._id || urlOrganisation?._id || "";
  const { isEnabled } = organisationTourData;

  const getTour = useCallback(() => {
    if (orgId) {
      api.organisations.getProductTour(
        orgId,
        tourId,
        ({ success, tour: newTour, userHasCompletedTour }) => {
          if (
            success &&
            newTour &&
            (!userHasCompletedTour || demoingTour === tourId)
          ) {
            setOrganisationTourData({ ...newTour, id: newTour.id ?? tourId });
          }
        },
        () => {},
      );
    }
  }, [orgId, tourId, demoingTour]);

  useEffect(() => {
    if (!theme.sizes.isMobile) {
      getTour();
    }
  }, [getTour, theme.sizes.isMobile]);

  const startTour = useCallback(() => {
    if (
      (demoingTour === tourId && isReady) ||
      (isReady && !completedTour && isEnabled)
    ) {
      setRunTour(true);
    }
  }, [isReady, completedTour, isEnabled, demoingTour, tourId]);

  useEffect(() => {
    startTour();
  }, [startTour]);

  const handleFinishTour = useCallback(() => {
    setCompletedTour(true);
    setRunTour(false);
    setTourStepIndex(0);
    if (onComplete) {
      onComplete();
    }
    if (demoingTour) {
      setDemoingTour("");
    } else {
      api.organisations.completeProductTour(
        orgId,
        tourId,
        () => {},
        () => {},
      );
    }
  }, [onComplete, orgId, tourId, demoingTour, setDemoingTour]);

  const handleJoyrideCallback = useCallback(
    (data: EventData, controls: Controls) => {
      const { action, index, status, type } = data;
      const step = data.step as ICustomStep;
      const { customTourFunctionId } = step;
      const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];
      const stepStatuses: string[] = [
        EVENTS.STEP_AFTER,
        EVENTS.TARGET_NOT_FOUND,
      ];

      // If opening a dialog, and there's a custom function assigned to the step, run the function
      if (action === "update" && status === "running") {
        if (
          customTourFunctionId &&
          customTourFunctions &&
          Object.hasOwn(customTourFunctions, customTourFunctionId)
        ) {
          const customTourFunction = customTourFunctions[customTourFunctionId];
          customTourFunction?.(step);
        }
      }

      if (finishedStatuses.includes(status)) {
        handleFinishTour();
      } else if (stepStatuses.includes(type)) {
        const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
        setTourStepIndex(nextStepIndex);
      }
    },
    [handleFinishTour, customTourFunctions],
  );

  const tour = useMemo((): ITour | null => {
    const foundTour = constants.productTours.find((pt) => pt.key === tourId) as
      | ITour
      | undefined;
    if (!foundTour) {
      return null;
    }
    return {
      ...foundTour,
      steps: foundTour.steps.map((step) => ({
        ...step,
        content: t(step.content),
      })),
    };
  }, [tourId, t]);

  if (!tour) {
    return null;
  }

  return (
    <div>
      <Joyride
        options={{ skipBeacon: true, scrollOffset: 250, zIndex: 1001 }}
        steps={tour?.steps ?? []}
        run={runTour}
        onEvent={handleJoyrideCallback}
        stepIndex={tourStepIndex}
        continuous
        initialStepIndex={0}
        tooltipComponent={(tooltipProps: TooltipRenderProps) => (
          <ProductTourTooltip
            {...tooltipProps}
            stepCount={tour?.steps?.length ?? 0}
          />
        )}
      />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  user: state.user,
  urlOrganisation: state.organisations.urlOrganisation,
  demoingTour: state.organisations.demoingTour,
});
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setDemoingTour: (tourId: string) =>
    dispatch(actions.organisations.setDemoingTour(tourId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductTour);
